import { useState } from 'react';
import { api, saveSession } from '../lib/api';
import { ErrorNote } from '../components/ui';

const LAST_STORE = 'njpos.registerStore';

export default function Login() {
  const params = new URLSearchParams(location.search);
  const registerStore = params.get('store') ?? localStorage.getItem(LAST_STORE);
  const [mode, setMode] = useState<'owner' | 'pin'>(registerStore && params.get('demo') !== '1' ? 'pin' : 'owner');
  // The sales website's "Try it live" link opens /?demo=1 with the public demo login filled in.
  const demoLink = params.get('demo') === '1';
  const [email, setEmail] = useState(demoLink ? 'admin' : '');
  const [password, setPassword] = useState(demoLink ? 'admin' : '');
  const [pin, setPin] = useState('');
  const [storeId, setStoreId] = useState(registerStore ?? '');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  async function ownerLogin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await api<any>('/api/auth/login', { body: { email, password } });
      if (r.stores[0]) localStorage.setItem(LAST_STORE, r.stores[0].id);
      saveSession({ token: r.token, user: r.user, stores: r.stores, storeId: r.stores[0]?.id ?? '' });
    } catch (err) { setError(err); } finally { setBusy(false); }
  }

  async function pinLogin(p: string) {
    setBusy(true); setError(null);
    try {
      const r = await api<any>('/api/auth/pin', { body: { storeId, pin: p } });
      localStorage.setItem(LAST_STORE, storeId);
      saveSession({ token: r.token, user: r.user, stores: r.stores, storeId });
      history.replaceState(null, '', '/register');
    } catch (err) { setError(err); setPin(''); } finally { setBusy(false); }
  }

  const press = (d: string) => {
    if (!busy) setPin((p) => (p + d).slice(0, 6));
  };

  return (
    <div className="login">
      <div className="card">
        <h1>PourPoint.AI</h1>
        <div className="tabs" role="tablist">
          <button role="tab" aria-selected={mode === 'owner'} className={mode === 'owner' ? 'on' : ''} onClick={() => setMode('owner')}>Owner sign-in</button>
          <button role="tab" aria-selected={mode === 'pin'} className={mode === 'pin' ? 'on' : ''} onClick={() => setMode('pin')}>Register PIN</button>
        </div>
        <ErrorNote error={error} />
        {mode === 'owner' ? (
          <form className="stack" onSubmit={ownerLogin}>
            {demoLink && <p className="muted">Demo store: username <b>admin</b>, password <b>admin</b>. Just press Sign in.</p>}
            <label>Email or username<input type="text" inputMode="email" autoCapitalize="none" spellCheck={false} autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
            <label>Password<input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
            <button className="primary big" disabled={busy}>Sign in</button>
          </form>
        ) : !storeId ? (
          <div className="stack-s">
            <p className="muted">This register isn’t linked to a store yet. An owner signs in once on this device, then cashiers can use their PIN.</p>
            <label>Store ID<input value={storeId} onChange={(e) => setStoreId(e.target.value.trim())} placeholder="Paste the store ID from Settings" /></label>
          </div>
        ) : (
          <div className="stack" tabIndex={0} autoFocus onKeyDown={(e) => {
            if (/^\d$/.test(e.key)) press(e.key);
            else if (e.key === 'Backspace') setPin((p) => p.slice(0, -1));
            else if (e.key === 'Enter' && pin.length >= 4) pinLogin(pin);
          }}>
            <div className="pin-dots" aria-live="polite">{'•'.repeat(pin.length) || ' '}</div>
            <div className="pin-pad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => <button key={d} onClick={() => press(d)}>{d}</button>)}
              <button onClick={() => setPin('')}>Clear</button>
              <button onClick={() => press('0')}>0</button>
              <button className="primary" disabled={pin.length < 4 || busy} onClick={() => pinLogin(pin)}>Enter</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
